import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
} from 'react';

import type {
  LimitExceededDetails,
  UsageMetricType,
} from '@superfunnel/types/usage-limits';

import { usePageLimitModal } from '../hooks/usePageLimitGate';
import { PageLimitMetric } from '../modals/PageLimitModal';
import { useSnackbarContext } from './SnackbarContext';

interface LimitExceededContextValue {
  handleLimitError: (details: LimitExceededDetails) => void;
  triggerLimitExceeded: (metric: UsageMetricType, message: string) => void;
}

const LimitExceededContext = createContext<LimitExceededContextValue>({
  handleLimitError: () => {},
  triggerLimitExceeded: () => {},
});

const PAGE_LIMIT_METRICS: UsageMetricType[] = [
  'pages',
  'publishedPages',
  'quizzes',
  'publishedQuizzes',
];

function isPageLimitMetric(metric: UsageMetricType): metric is PageLimitMetric {
  return PAGE_LIMIT_METRICS.includes(metric);
}

export function LimitExceededProvider({ children }: { children: ReactNode }) {
  const { show } = useSnackbarContext();
  const { showPageLimit, pageLimitModal } = usePageLimitModal();

  const handleLimitError = useCallback(
    (details: LimitExceededDetails) => {
      if (isPageLimitMetric(details.metric)) {
        showPageLimit({
          metric: details.metric,
          current: details.current,
          limit: details.limit,
        });
        return;
      }

      show(details.message, 'warn', {
        disableAutoClose: true,
        buttons: [
          {
            title: 'Upgrade Plan',
            onClick: () => {
              window.location.href = '/settings/subscription';
            },
          },
        ],
      });
    },
    [show, showPageLimit],
  );

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<LimitExceededDetails>).detail;
      if (detail) {
        handleLimitError(detail);
      }
    };
    window.addEventListener('sf:limit-exceeded', handler);
    return () => window.removeEventListener('sf:limit-exceeded', handler);
  }, [handleLimitError]);

  const triggerLimitExceeded = useCallback(
    (metric: UsageMetricType, message: string) => {
      handleLimitError({
        code: 'LIMIT_EXCEEDED',
        metric,
        current: 0,
        limit: 0,
        message,
      });
    },
    [handleLimitError],
  );

  return (
    <LimitExceededContext.Provider
      value={{ handleLimitError, triggerLimitExceeded }}
    >
      {children}
      {pageLimitModal}
    </LimitExceededContext.Provider>
  );
}

export function useLimitExceeded() {
  return useContext(LimitExceededContext);
}
